import { useEffect, useState } from "react";
import { useInstructorVoice } from "@/hooks/useInstructorVoice";
import type { InstructorId } from "@/lib/audio/instructorVoiceMap";
import aelyraAvatar from "@/assets/instructors/avatars/aelyra-avatar.png";
import caelionAvatar from "@/assets/instructors/avatars/caelion-avatar.png";
import vaelorAvatar from "@/assets/instructors/avatars/vaelor-avatar.png";
import lumiAvatar from "@/assets/instructors/avatars/lumi-avatar.png";

export const instructorPortraitMap: Record<
  InstructorId,
  { name: string; portrait: string; accentClass: string }
> = {
  aelyra: {
    name: "Aelyra Solari",
    portrait: aelyraAvatar,
    accentClass: "from-cyan-300/60 to-violet-400/50",
  },
  caelion: {
    name: "Caelion Veyr",
    portrait: caelionAvatar,
    accentClass: "from-blue-300/60 to-cyan-400/50",
  },
  vaelor: {
    name: "Headmaster Vaelor",
    portrait: vaelorAvatar,
    accentClass: "from-amber-300/60 to-orange-400/50",
  },
  lumi: {
    name: "Lumi Vanta",
    portrait: lumiAvatar,
    accentClass: "from-fuchsia-300/60 to-cyan-300/50",
  },
};

/** Decode every instructor avatar so the bitmap is ready before the first caption. */
export function preloadInstructorAvatars(): void {
  if (typeof window === "undefined") return;
  for (const { portrait } of Object.values(instructorPortraitMap)) {
    const img = new Image();
    img.decoding = "sync";
    img.src = portrait;
    img.decode?.().catch(() => {});
  }
}

// Kick off preload at module evaluation (the InstructorVoiceProvider also
// calls preloadInstructorAvatars() on mount as a belt-and-suspenders guard).
preloadInstructorAvatars();

/**
 * Floating mentor capsule for the currently-playing instructor voice line.
 * Anchored to the lower-left of the module scene with a circular portrait
 * that breaks out of the left edge of the capsule.
 */
export default function InstructorVoiceCaption() {
  const { speakingMomentId, getMoment, getInstructorLabel } = useInstructorVoice();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(Boolean(speakingMomentId));
  }, [speakingMomentId]);

  if (!speakingMomentId) return null;
  const moment = getMoment(speakingMomentId);
  if (!moment) return null;

  const meta = instructorPortraitMap[moment.instructor];
  const portrait = meta?.portrait;
  const accent = meta?.accentClass ?? "from-primary/50 to-accent/40";
  const name = getInstructorLabel(moment.instructor) ?? meta?.name ?? "";

  return (
    <div
      aria-live="polite"
      role="status"
      className={`pointer-events-none fixed z-40 transition-all duration-300 ease-out
        left-1/2 -translate-x-1/2 top-[max(env(safe-area-inset-top),3.5rem)] w-[calc(100%-2rem)] max-w-[420px]
        md:left-[clamp(20px,5vw,72px)] md:right-auto md:translate-x-0 md:top-[clamp(96px,18vh,180px)] md:w-[min(360px,32vw)] md:max-w-[360px]
        ${visible ? "opacity-100 translate-y-0" : "translate-y-2 opacity-0"}`}
    >
      <div className="pointer-events-auto relative rounded-3xl border border-primary/25 bg-[hsl(var(--background)/0.88)] px-4 pt-10 pb-4 shadow-[0_18px_60px_-18px_hsl(var(--primary)/0.6)] backdrop-blur-xl md:px-5 md:pt-12 md:pb-5">
        {/* Portrait breaking out of the top-left corner */}
        <div className="absolute -left-3 -top-5 md:-left-4 md:-top-6">
          <div className="relative">
            <div
              className={`absolute -inset-1 rounded-full bg-gradient-to-br ${accent} opacity-80 blur-md`}
              aria-hidden
            />
            {portrait ? (
              <img
                src={portrait}
                alt={name}
                width={56}
                height={56}
                loading="eager"
                decoding="sync"
                fetchPriority="high"
                className="relative h-12 w-12 rounded-full border border-primary/40 object-cover shadow-[0_4px_18px_-4px_hsl(var(--primary)/0.6)] md:h-16 md:w-16"
              />
            ) : (
              <div
                className={`relative h-12 w-12 rounded-full border border-primary/40 bg-gradient-to-br ${accent} md:h-16 md:w-16`}
              />
            )}
          </div>
        </div>
        <div className="min-w-0">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-[0.2em] text-primary/90">
            {name}
          </div>
          <div className="text-sm leading-snug text-foreground/95 md:text-[14.5px]">
            {moment.transcript}
          </div>
        </div>
      </div>
    </div>
  );
}